import type { Device, ExternalDevice } from "@expert/shared-types";

import { createImmerStore } from "@expert/state-utils";
import { freeze } from "immer";

/**
 * Which question produced the device — the basis for precedence. Not the same as the `ozmo`/`asurion`
 * catalog source. `incidental` is harvested off whatever the expert asked, so it may describe the
 * handset being replaced rather than the one being activated.
 */
export type DeviceInfoOrigin = "probe" | "incidental";

/** GAIA's two device arrays, kept separate because they are independently sourced and can disagree. */
export interface SessionDeviceInfo {
    externalDevices: ExternalDevice[];
    detectedDevices: Device[];
    origin: DeviceInfoOrigin;
}

const EMPTY_DEVICE_INFO: SessionDeviceInfo = freeze({
    externalDevices: [],
    detectedDevices: [],
    origin: "incidental" as DeviceInfoOrigin,
});

interface DeviceInfoStore {
    sessions: Record<string, SessionDeviceInfo>;
    getDeviceInfo: (sessionId: string) => SessionDeviceInfo;
    setDevices: (
        sessionId: string,
        devices: Pick<Partial<SessionDeviceInfo>, "externalDevices" | "detectedDevices">,
        origin: DeviceInfoOrigin,
    ) => void;
    resetDevices: (sessionId: string) => void;
}

export const useDeviceInfoStore = createImmerStore<DeviceInfoStore>({
    devtools: {
        store: "device-info",
        name: "workspace/device-info",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    sessions: {},

    getDeviceInfo: (sessionId: string) => get().sessions[sessionId] ?? EMPTY_DEVICE_INFO,

    setDevices: (
        sessionId: string,
        devices: Pick<Partial<SessionDeviceInfo>, "externalDevices" | "detectedDevices">,
        origin: DeviceInfoOrigin,
    ) => {
        set((state) => {
            const existing = state.sessions[sessionId];

            // Precedence is by origin, not arrival order — otherwise an unrelated question late in the
            // call overwrites a correct probe result and the flow's device changes under the expert.
            if (existing?.origin === "probe" && origin === "incidental") {
                return;
            }

            // A probe replaces incidental data wholesale rather than filling per-field: the two may
            // describe different devices, and merging would compose one that never existed.
            const base =
                origin === "probe" && existing?.origin === "incidental"
                    ? { externalDevices: [], detectedDevices: [] }
                    : (existing ?? { externalDevices: [], detectedDevices: [] });

            state.sessions[sessionId] = {
                // Within one origin, keep a list the response didn't carry — GAIA populates them separately.
                externalDevices: devices.externalDevices?.length ? devices.externalDevices : base.externalDevices,
                detectedDevices: devices.detectedDevices?.length ? devices.detectedDevices : base.detectedDevices,
                origin,
            };
        });
    },

    resetDevices: (sessionId: string) => {
        set((state) => {
            delete state.sessions[sessionId];
        });
    },
}));
